import Type from 'src/Type';
import config from 'src/utils/config';
import Primitive from 'src/utils/Primitive';

import Node from '../Node';

import type Context from 'src/Context';
import type { Expression } from 'src/types';

export interface CreateCompanyNodeT {
    $type: 'CreateCompanyNode';
    name: Expression;
    type: Expression;
    data: Expression;
}

const companyTypes = [
    { key: 'brand', label: 'brand' },
    { key: 'broker', label: 'broker' },
    { key: 'distributor', label: 'distributor' },
    { key: 'manufacturer', label: 'manufacturer' },
    { key: 'other', label: 'other' },
    { key: 'retailer', label: 'retailer' },
] as const;

export default class CreateCompanyNode extends Node<CreateCompanyNodeT> {
    static TYPE = Type.Object(
        {
            key: 'name',
            label: 'Company name',
            type: 'StringNode',
            suggestions: 'string',
        },
        {
            key: 'type',
            label: 'Type',
            type: 'StringNode',
            suggestions: companyTypes,
            forceSuggestion: true,
        },
        { key: 'data', label: 'Data', type: 'ObjectNode' },
    );

    static uiConfig = {
        ...config.presets.tasker,
        description: 'Create an entirely new Company document',
    };

    static getDefault(): Expression {
        return {
            ...super.getDefault(),
            name: this.getDefaultFor('StringNode'),
            type: Primitive.string(''),
            data: {
                ...this.getDefaultFor('ObjectNode'),
            },
        };
    }

    async execute(context: Context): Promise<void> {
        const data = await context.evaluateLog('data', this.expression.data);
        const name = await context.evaluateLog('name', this.expression.name);
        const type = await context.evaluateLog('type', this.expression.type);

        if (!data || typeof data !== 'object') throw new Error('Company must be an object');

        // bias towards the 'name' and 'type' fields
        if (name) data.name = name;
        if (type) data.type = type;

        context.addTaskerAction({
            kind: 'createCompany',
            data,
        });
    }
}
